import { FormikProps } from 'formik';
import React from 'react';
import { useSelector } from 'react-redux';
import { ExternalLink } from '../../../../global/components/external-link-modal';
import { FormField } from '../../../../global/components/form-element/form-field';
import { NumberField, NumberInputTypes } from '../../../../global/components/form-element/number-field';
import { RegularExpressions } from '../../../../global/constants/regular-expressions';
import { midyearSelectionStyles } from '../../../../global/constants/theme/styles/pages/mid-year/midyear-selection-layout';
import { getCurrencyFormat } from '../../../../global/helpers/format-helpers';
import { LabelSettingProps } from '../../../../global/models/epi-model';
import { TcsmStates } from '../../../../root/tcsm-model';
import { getTierLabel } from '../../../annual-enrollment/helpers/annual-enrollment-helpers';
import { AEOffering, Enrollment } from '../../../annual-enrollment/models/annual-enrollment-models';
import { None } from '../../constants/constant-strings';
import { AnnualEnrollmentProps, MidYearProps } from '../../models/arc-epi-props';
import { BillingAccount } from '../billing-accounts/billing-accounts-models';
import { MidYearEnrollmentFormValue, MidYearPlanType } from './mid-year-model';

interface Props {
	title: string;
	annualEnrollmentSettings: AnnualEnrollmentProps;
	midYearSettings: MidYearProps;
	billingAccounts: BillingAccount[];
	offer: AEOffering;
	selectedEnrollment: Enrollment;
	defaultEnrollment: Enrollment;
	planType: MidYearPlanType;
	formikProps: FormikProps<MidYearEnrollmentFormValue>;
	propName: string;
	disableVolume?: boolean;
	midYearLocked?: boolean;
	handleChangeDisplayCafeteria?:any;
	additionalMessage?: string;
	disabled?: boolean;
}

export const MidYearPlanSelection = (props: React.PropsWithChildren<Props>) => {
	const { midYearLocked, disableVolume, handleChangeDisplayCafeteria } = props;
	const labelSetting = useSelector<TcsmStates, LabelSettingProps>(state => state.epiProps.labelSettingState.epiContent);
	const classes = midyearSelectionStyles();
	const hadPlan = (props?.defaultEnrollment?.code && props?.defaultEnrollment?.code !== None);
	const planChanged = props.defaultEnrollment?.code && props.selectedEnrollment?.code !== props.defaultEnrollment?.code && hadPlan;
	const billingAccountChanged = props.selectedEnrollment?.account_nbr && props.defaultEnrollment?.account_nbr && props.selectedEnrollment?.account_nbr !== props.defaultEnrollment?.account_nbr && hadPlan;
	const tierChanged = props.selectedEnrollment?.tier && props.selectedEnrollment?.tier !== props.defaultEnrollment?.tier && hadPlan;
	const rateChanged = props.selectedEnrollment?.rate && props.selectedEnrollment?.rate !== props.defaultEnrollment?.rate && hadPlan;
	const effectiveDateChanged = (props.selectedEnrollment?.effective_date ? new Date(props.selectedEnrollment?.effective_date) : new Date()).toISOString().split('T')[0] !== (props.defaultEnrollment?.effective_date ? new Date(props.defaultEnrollment?.effective_date) : new Date()).toISOString().split('T')[0] && hadPlan;
	const showTier = props.planType === MidYearPlanType.Dental || props.planType === MidYearPlanType.Medical;

	const isUnemployedInsurance = props.planType === MidYearPlanType.Unemployment;
	const billingAccountUE = props.billingAccounts?.filter((b) => b.account_number.match(RegularExpressions.BillingAccountUE));
	const billingAccountNonUE = props.billingAccounts?.filter((b) => !b.account_number.match(RegularExpressions.BillingAccountUE));
	const billingAccountFilteredOptions = isUnemployedInsurance ? billingAccountUE : billingAccountNonUE;

	const billingAccountOptions = billingAccountFilteredOptions?.map(a => { return { text: <div className='midyear-selection-plan-item-dropdown-option '>{a.account_number} {a.account_name} <div><span className='input-caption'><em>{a.billing_city}</em></span></div></div>, value: a.account_number } /*(<div> TEST option</div>)*/; }) || [];

	function IncludeSelectedPlanIfNotPresent(planOptions: any[], defaultEnrollment: Enrollment) {
		if (planOptions?.length > 0 && hadPlan && !planOptions.find(o => (o?.code && o.code === defaultEnrollment?.code) || (o?.value && o.value === defaultEnrollment?.code))) {
			planOptions.unshift({
				text: defaultEnrollment?.name || defaultEnrollment.code,
				value: defaultEnrollment.code,
			});
		}

		return planOptions;
	}

	React.useEffect(() => {
		if (props.disabled) {
			props.formikProps.setFieldValue('longTermDisabilityPlan', '');
			props.formikProps.setFieldValue('longTermDisabilityPlanBillingAccount', '');
			props.formikProps.setFieldValue('longTermDisabilityPlanEffectiveDate', null);
			props.formikProps.setFieldValue('longTermDisabilityPlanTerminateDate', null);
			props.formikProps.setFieldValue('shortTermDisabilityPlan', '');
			props.formikProps.setFieldValue('shortTermDisabilityPlanBillingAccount', '');
			props.formikProps.setFieldValue('shortTermDisabilityPlanEffectiveDate', null);
			props.formikProps.setFieldValue('shortTermDisabilityPlanTerminateDate', null);
		}
	}, [props.disabled]);

	const planOptions = IncludeSelectedPlanIfNotPresent(props.offer?.plans?.map(a => { return { text: a.name, value: a.code }; }) || [], props.defaultEnrollment);

	const renderVolume = () => {
		return (

			disableVolume || props.formikProps?.values[`${props.propName}`] === None || props.formikProps?.values[`${props.propName}BillingAccount`] === None ?
				<p className='input-label'>{props.midYearSettings.volumeLabel}</p> :
				<div className='midyear-selection-plan-item'>
					<NumberField
						name={`${props.propName}Volume`}
						label={props.midYearSettings.volumeLabel}
						type={NumberInputTypes.Currency}
						formikProps={props.formikProps}
						disabled={midYearLocked}
					/>
				</div>
		);
	};

	const renderMSPFlag = () => {

		if (!props.selectedEnrollment?.code ||
			(props.selectedEnrollment?.code === props?.defaultEnrollment?.code))
			return null;

		const planSelected = planOptions.find(({ value }) => value === props.selectedEnrollment?.code);
		const planDefault = planOptions.find(({ value }) => value === props?.defaultEnrollment?.code);

		if (!planSelected?.text?.includes('MSP') ||
			planDefault?.text?.includes('MSP'))
			return null;

		return (
			<div className='midyear-selection-plan-item-sublabel'>
				<p className='midyear-selection-plan-item-sublabel-heading caption'><strong>{props?.midYearSettings.mspPlanTitle}</strong></p>
				<p><ExternalLink linkIcon={<svg aria-hidden='true' focusable='false'><use xmlnsXlink='http://www.w3.org/2000/xlink' xlinkHref='#msp-icon' /></svg>} linkText={props?.midYearSettings.mspPlanLinkLabel} linkUrl={props?.midYearSettings?.mspPlanLinkUrl} /></p>
			</div>
		);
	};

	const renderSelection = () => {
		if (!props.offer?.plans || props.offer.plans.length < 1) {
			return (
				<p>{props.midYearSettings?.noPlansProvidedMessage || 'There are no plans provided by this Benefits Group.'}</p>
			);
		}

		return (
			<div className='midyear-selection'>
				<div className={`midyear-selection-plan-item ${(billingAccountChanged && props?.defaultEnrollment?.account_nbr) ? 'midyear-selection-plan-item-edited' : ''}`}>
					<FormField
						name={`${props.propName}BillingAccount`}
						label={props.midYearSettings?.billingAccountLabel}
						component='input'
						options={billingAccountOptions}
						errors={props.formikProps.errors}
						touched={props.formikProps.touched}
						select
						placeHolder={labelSetting.selectDefaultText}
						subLabel={(billingAccountChanged && props?.defaultEnrollment?.account_nbr) && <p className='input-label midyear-selection-plan-item-text-new'><em>{props.midYearSettings?.newText}</em></p>}
						disabled={midYearLocked || props.disabled}
						onchange={handleChangeDisplayCafeteria(props.formikProps.values)}
					/>
					{
						(billingAccountChanged && props?.defaultEnrollment?.account_nbr) && <p className='midyear-selection-plan-item-text-original caption'><strong>{props?.midYearSettings?.originalText} {props?.midYearSettings?.billingAccountLabel}</strong>: {props?.defaultEnrollment?.account_nbr}</p>
					}
				</div>
				<div className={`midyear-selection-plan-item ${(planChanged && props?.defaultEnrollment?.code) ? 'midyear-selection-plan-item-edited' : ''}`}>
					<FormField
						name={`${props.propName}`}
						label={props.midYearSettings?.planLabel}
						component='input'
						options={planOptions}
						errors={props.formikProps.errors}
						touched={props.formikProps.touched}
						select
						placeHolder={labelSetting.selectDefaultText}
						subLabel={(planChanged && props?.defaultEnrollment?.code) && <p className='input-label midyear-selection-plan-item-text-new'><em>{props.midYearSettings?.newText}</em></p>}
						disabled={midYearLocked || props.disabled}
						onchange={handleChangeDisplayCafeteria(props.formikProps.values)}
					/>
					{
						(planChanged && props?.defaultEnrollment?.code) && <p className='midyear-selection-plan-item-text-original caption'><strong>{props?.midYearSettings?.originalText} {props?.midYearSettings?.planLabel}</strong>: {props?.offer?.plans?.filter(a => a.code === props.defaultEnrollment?.code)[0]?.name}</p>
					}
					{renderMSPFlag()}
				</div>
				{
					!showTier ?
						renderVolume() :
						<div className='midyear-selection-plan-item'>
							<div className='midyear-selection-plan-item-header'>
								<p className='input-label midyear-selection-plan-item-label'>{props?.midYearSettings?.tierLabel}</p>
								<p className='input-label midyear-selection-plan-item-text-new'><em>{tierChanged && props?.midYearSettings?.newText}</em></p>
							</div>
							{props?.selectedEnrollment?.tier && getTierLabel(props?.selectedEnrollment?.tier, props.annualEnrollmentSettings)}
							{(tierChanged && props.defaultEnrollment.tier) && <p className='midyear-selection-plan-item-text-original caption'><strong>{props?.midYearSettings?.originalText} {props?.midYearSettings?.tierLabel}</strong>: ${props?.defaultEnrollment?.tier}</p>}

						</div>

				}

				<div className='midyear-selection-plan-item rate'>
					<div className='midyear-selection-plan-item-header'>
						<p className='input-label midyear-selection-plan-item-label'>{props.midYearSettings?.rateLabel}</p>
						{rateChanged && <p className='input-label midyear-selection-plan-item-text-new'><em>{props?.midYearSettings?.newText}</em></p>}
					</div>
					<p>{props.selectedEnrollment?.rate && getCurrencyFormat(parseFloat(props.selectedEnrollment?.rate))}</p>
					{(rateChanged && props.defaultEnrollment?.rate) && <p className='midyear-selection-plan-item-text-original caption'><strong>{props?.midYearSettings?.originalText} {props?.midYearSettings?.rateLabel}</strong>: {getCurrencyFormat(parseFloat(props?.defaultEnrollment?.rate))}</p>}
				</div>

				<div className={`midyear-selection-plan-item ${effectiveDateChanged ? 'midyear-selection-plan-item-edited' : ''}`}>
					<FormField
						name={`${props.propName}EffectiveDate`}
						label={props.midYearSettings.effectiveDateLabel}
						component='datePicker'
						touched={props.formikProps.touched}
						errors={props.formikProps.errors}
						autoWidth
						tooltip={{
							title: props.midYearSettings.effectiveDateHelperText,
						}}
						subLabel={effectiveDateChanged && <p className='input-label midyear-selection-plan-item-text-new'><em>{props.midYearSettings.newText}</em></p>}
						disabled={midYearLocked || props.disabled}
						onchange={handleChangeDisplayCafeteria(props.formikProps.values)}
					/>
					{(effectiveDateChanged && props?.defaultEnrollment?.effective_date) && <p className='midyear-selection-plan-item-text-original caption'><strong>{props?.midYearSettings?.originalText} {props?.midYearSettings?.effectiveDateLabel}</strong>: {props.defaultEnrollment?.effective_date}</p>}
				</div>
				<div className='midyear-selection-plan-item'>
					<FormField
						name={`${props.propName}TerminateDate`}
						label={props.midYearSettings.terminateDateLabel}
						component='datePicker'
						touched={props.formikProps.touched}
						errors={props.formikProps.errors}
						autoWidth
						tooltip={{
							title: props.midYearSettings.terminateDateHelperText,
						}}
						disabled={midYearLocked || props.disabled}
						onchange={handleChangeDisplayCafeteria(props.formikProps.values)}
					/>
				</div>
			</div>
		);
	};


	return (
		<div className={`${classes.midYearSelectionContainer} midyear-selection-section`}>
			<h3 className='midyear-selection-header'>{props.title}</h3>
			{props.additionalMessage && <p className='midyear-selection-additional-message'>{props.additionalMessage}</p>}
			{renderSelection()}
		</div>

	);
};


MidYearPlanSelection.defaultProps = {
	disableVolume: false,
	midYearLocked: false,
	handleChangeDisplayCafeteria: null,
	additionalMessage: null,
	disabled: false,
};